import { useFlag } from '@reflag/react-sdk';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import { AUDIT_LOGS_FEATURE_FLAG } from '@gitbook/app-featureflags';
import type {
    NavigationAccountSettingsTab,
    NavigationOrgSettingsMainTab,
    NavigationScreenAccountSettings,
    NavigationScreenOrgSettings,
} from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    Sidebar,
    SidebarBody,
    SidebarEmptyState,
    SidebarHeader,
    SidebarItemList,
    SidebarLink,
    SidebarSection,
    SidebarSectionHeading,
    SidebarToggleButton,
} from '@gitbook/spine-react';

import { LayoutSidebarContext } from '../../AppLayout';
import { getLastSeenOrgId } from '../../navigation';
import {
    apiQuery,
    desktopSidebarCollapsedAtom,
    getTrialingOrganizationFeaturePlan,
    useIsSidebarLocked,
    useUnlockSidebar,
} from '../../state';
import { SidebarTrialingBadge } from '../SidebarTrialingBadge';

/**
 * Sidebar for settings pages.
 */
export function SettingsSidebar(props: {
    screen: NavigationScreenAccountSettings | NavigationScreenOrgSettings;
}) {
    const { screen } = props;
    const {
        data: { items: userOrganizations },
    } = useSuspenseQuery(apiQuery('listOrganizationsForAuthenticatedUser', { limit: 100 }));

    useAutoOpenSidebar();
    const organization = useSettingsOrganization({ userOrganizations, screen });

    return (
        <Sidebar>
            <SettingsSidebarHeader organizationId={organization?.id} />

            <SidebarBody>
                <AccountSection screen={screen} />
                {organization && (
                    <OrganizationSection screen={screen} organization={organization} />
                )}
            </SidebarBody>
        </Sidebar>
    );
}

function SettingsSidebarHeader(props: { organizationId?: string }) {
    const [isCollapsed, setCollapse] = useAtom(desktopSidebarCollapsedAtom);
    const isLocked = useIsSidebarLocked();
    const unlock = useUnlockSidebar();
    const isSidebarClosed = isLocked || isCollapsed;

    return (
        <SidebarHeader>
            <div className="flex w-full items-center gap-2">
                <div className="min-w-0 flex-1">
                    <BackLink organizationId={props.organizationId} />
                </div>

                {isSidebarClosed ? (
                    <SidebarToggleButton
                        isCollapsed={isSidebarClosed}
                        onCollapse={() => {
                            setCollapse(true);
                        }}
                        onUncollapse={() => {
                            unlock();
                            setCollapse(false);
                        }}
                        outlined={false}
                    />
                ) : null}
            </div>
        </SidebarHeader>
    );
}

function BackLink(props: { organizationId?: string }) {
    return (
        <SidebarLink
            label="Back to app"
            data-testid="settings.closeButton"
            icon={Icon.ArrowLeft}
            href={{
                back: 'app',
                screen: {
                    type: 'home',
                    organization: props.organizationId || undefined,
                },
            }}
        />
    );
}

/**
 * Get the organization ID to use in the settings screen.
 */
function useSettingsOrganization(props: {
    userOrganizations: api.Organization[];
    screen: NavigationScreenAccountSettings | NavigationScreenOrgSettings;
}): api.Organization | null {
    const { userOrganizations, screen } = props;
    return React.useMemo(() => {
        // Get the first accessible organization
        // If the user has no organizations, we return null
        const firstOrg = userOrganizations[0];
        if (!firstOrg) {
            return null;
        }

        // Organization is defined in the screen
        const orgIdFromScreen = 'organization' in screen ? screen.organization : null;
        if (orgIdFromScreen) {
            const orgFromScreen = userOrganizations.find((org) => org.id === orgIdFromScreen);
            if (orgFromScreen) {
                return orgFromScreen;
            }
        }

        // Organization is the last seen organization
        const lastSeenOrgId = getLastSeenOrgId();
        if (lastSeenOrgId) {
            const lastSeenOrg = userOrganizations.find((org) => org.id === lastSeenOrgId);
            if (lastSeenOrg) {
                return lastSeenOrg;
            }
        }

        // Organization is the first accessible organization
        return firstOrg;
    }, [userOrganizations, screen]);
}

/**
 * Automatically open the sidebar.
 */
function useAutoOpenSidebar() {
    const { isCollapsed } = React.useContext(LayoutSidebarContext);
    const setSidebarCollapsed = useSetAtom(desktopSidebarCollapsedAtom);
    React.useEffect(() => {
        if (isCollapsed) {
            // we always want the settings sidebar to be open on desktop
            // so we force it open if it's not
            setSidebarCollapsed(false);
        }
    }, [isCollapsed, setSidebarCollapsed]);
}

function AccountSection(props: {
    screen: NavigationScreenAccountSettings | NavigationScreenOrgSettings;
}) {
    const { screen } = props;

    const navigationPropsToTab = (tab: NavigationAccountSettingsTab) => {
        return {
            href: {
                current: {
                    type: 'account',
                    tab,
                },
            } as const,
            isActive: screen.type === 'account' && screen.tab === tab,
        };
    };

    return (
        <SidebarSection>
            <SidebarSectionHeading>Account</SidebarSectionHeading>
            <SidebarItemList>
                <SidebarLink
                    label="General"
                    icon={Icon.Settings}
                    {...navigationPropsToTab('profile')}
                />
                <SidebarLink
                    label="Notifications"
                    icon={Icon.Bell}
                    {...navigationPropsToTab('notification')}
                />
                <SidebarLink
                    label="Organizations"
                    icon={Icon.Organization}
                    {...navigationPropsToTab('organizations')}
                />
                <SidebarLink
                    label="Developer tools"
                    icon={Icon.Code}
                    {...navigationPropsToTab('developer')}
                />
            </SidebarItemList>
        </SidebarSection>
    );
}

function OrganizationSection(props: {
    screen: NavigationScreenAccountSettings | NavigationScreenOrgSettings;
    organization: api.Organization;
}) {
    const { screen, organization } = props;

    const auditLogsFeature = useFlag(AUDIT_LOGS_FEATURE_FLAG);
    const trialingMergeRulesPlan = getTrialingOrganizationFeaturePlan(
        organization,
        api.OrganizationFeature.ChangeRequestsMergeRules
    );
    const trialingTranslationsPlan = getTrialingOrganizationFeaturePlan(
        organization,
        api.OrganizationFeature.Translations
    );
    const trialingTeamsPlan = getTrialingOrganizationFeaturePlan(
        organization,
        api.OrganizationFeature.MembersTeams
    );

    const navigationPropsToTab = (tab: NavigationOrgSettingsMainTab) => {
        return {
            href: {
                current: {
                    type: 'org_settings',
                    organization: organization.id,
                    tab,
                },
            } as const,
            isActive:
                screen.type === 'org_settings'
                    ? screen.organization === organization.id && screen.tab.type === tab.type
                    : false,
        };
    };

    const isAdmin = organization.permissions.admin;
    const showIntegrations = organization.permissions.listIntegrations;
    // List members and is the closest non-guest permissions we can use here
    const showOpenAPI = organization.permissions.listMembers;
    const showTranslations = organization.permissions.listMembers;

    const hasAnyItem = isAdmin || showIntegrations || showOpenAPI || showTranslations;

    return (
        <SidebarSection>
            <SidebarSectionHeading>Organization</SidebarSectionHeading>
            {hasAnyItem ? (
                <SidebarItemList>
                    {isAdmin ? (
                        <SidebarLink
                            label="General"
                            icon={Icon.Organization}
                            {...navigationPropsToTab({ type: 'profile' })}
                        />
                    ) : null}
                    {isAdmin ? (
                        <SidebarLink
                            label="Members"
                            icon={Icon.Users}
                            {...navigationPropsToTab({ type: 'members' })}
                        />
                    ) : null}
                    {isAdmin && organization.permissions.ownTeam ? (
                        <SidebarLink
                            label="Teams"
                            icon={Icon.Team}
                            {...navigationPropsToTab({ type: 'teams' })}
                        >
                            <SidebarTrialingBadge plan={trialingTeamsPlan} />
                        </SidebarLink>
                    ) : null}
                    {isAdmin ? (
                        <SidebarLink
                            label="Access"
                            icon={Icon.Sso}
                            {...navigationPropsToTab({ type: 'sso' })}
                        />
                    ) : null}
                    {isAdmin && organization.auditLogs && auditLogsFeature.isEnabled ? (
                        <SidebarLink
                            label="Audit logs"
                            icon={Icon.History}
                            {...navigationPropsToTab({ type: 'audit-logs' })}
                        >
                            <Badge kind="beta" size="xxsmall" label="Beta" className="-ml-1" />
                        </SidebarLink>
                    ) : null}
                    {isAdmin ? (
                        <SidebarLink
                            label="Merge rules"
                            icon={Icon.MergeRulesAllow}
                            {...navigationPropsToTab({ type: 'rules' })}
                        >
                            <SidebarTrialingBadge plan={trialingMergeRulesPlan} />
                        </SidebarLink>
                    ) : null}
                    {isAdmin ? (
                        <SidebarLink
                            label="GitBook Agent"
                            icon={Icon.AIAgent}
                            {...navigationPropsToTab({ type: 'agents' })}
                        />
                    ) : null}
                    {showIntegrations ? (
                        <SidebarLink
                            label="Integrations"
                            icon={Icon.Integrations}
                            {...navigationPropsToTab({ type: 'integrations' })}
                        />
                    ) : null}
                    {showOpenAPI ? (
                        <SidebarLink
                            label="OpenAPI"
                            icon={Icon.OpenAPI}
                            {...navigationPropsToTab({ type: 'openapi' })}
                        />
                    ) : null}
                    {showTranslations ? (
                        <SidebarLink
                            label="Translations"
                            icon={Icon.AITranslate}
                            {...navigationPropsToTab({ type: 'translations', view: 'overview' })}
                        >
                            <SidebarTrialingBadge plan={trialingTranslationsPlan} />
                        </SidebarLink>
                    ) : null}
                    {isAdmin ? (
                        <SidebarLink
                            label="Invite links"
                            icon={Icon.Link}
                            {...navigationPropsToTab({ type: 'invites' })}
                        />
                    ) : null}
                    {isAdmin ? (
                        <SidebarLink
                            label="Billing"
                            icon={Icon.Upgrade}
                            {...navigationPropsToTab({ type: 'plans' })}
                        />
                    ) : null}
                </SidebarItemList>
            ) : (
                <SidebarEmptyState>
                    You don’t have admin access to that organization.
                </SidebarEmptyState>
            )}
        </SidebarSection>
    );
}
